import { useEffect, useState } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import AdminTrivia from './AdminTrivia';
import AdminJeopardy from './AdminJeopardy';
import './Admin.css';

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
  explainer?: string;
}

function extractSecret(): string {
  const m = window.location.pathname.match(/^\/admin\/(.+)$/);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function Admin() {
  useEffect(() => { document.title = 'Gamy · Admin'; }, []);
  const [tab, setTab] = useState<'trivia' | 'jeopardy'>('trivia');
  const [triviaQuestions, setTriviaQuestions] = useState<TriviaQuestion[]>([]);
  const [gameData, setGameData] = useState<any>(null);
  // null = checking, true = authorized, false = denied
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const secret = extractSecret();

  const { send, connected } = useGameSocket((msg) => {
    if (msg.type === 'ADMIN_AUTH_RESULT') {
      setAuthorized(!!msg.ok);
      return;
    }
    if (msg.type !== 'STATE') return;
    if (Array.isArray(msg.triviaQuestions)) setTriviaQuestions(msg.triviaQuestions);
    if (msg.gameData) setGameData(msg.gameData);
  });

  // On (re)connect, present our URL secret for auth.
  useEffect(() => {
    if (connected) {
      setAuthorized(null);
      send({ type: 'ADMIN_AUTH', secret });
    }
  }, [connected, send, secret]);

  // Wrap send to automatically attach the secret to mutation messages.
  // (The server uses the per-connection auth flag, so the secret is
  // technically not re-checked, but including it costs nothing and lets the
  // server log mismatches if we ever want to.)
  function adminSend(msg: any) {
    send({ ...msg, adminSecret: secret });
  }

  const banner = authorized === false ? (
    <div className="admin-denied">
      <strong>Access denied.</strong> This URL is missing or has an incorrect admin secret. Edits will not be saved. Ask the host for the correct link.
    </div>
  ) : authorized === null ? (
    <div className="admin-checking">Checking access…</div>
  ) : null;

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1 className="admin-title">Gamy — Question Manager</h1>
        <div className={`admin-conn ${connected ? 'admin-conn-ok' : 'admin-conn-bad'}`}>
          {connected ? '● connected' : '○ connecting…'}
        </div>
      </header>

      {banner}

      <nav className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'trivia' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('trivia')}
        >
          Party Trivia ({triviaQuestions.length})
        </button>
        <button
          className={`admin-tab ${tab === 'jeopardy' ? 'admin-tab-active' : ''}`}
          onClick={() => setTab('jeopardy')}
        >
          Jeopardy Game
        </button>
      </nav>

      <main className="admin-main" style={{ opacity: authorized === true ? 1 : 0.55, pointerEvents: authorized === true ? 'auto' : 'none' }}>
        {tab === 'trivia' && (
          <AdminTrivia questions={triviaQuestions} send={adminSend} />
        )}
        {tab === 'jeopardy' && (
          <AdminJeopardy gameData={gameData} send={adminSend} />
        )}
      </main>
    </div>
  );
}
